import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Bookmark, Sparkles, CalendarClock, User, Bell,
  LogOut, MapPin, IndianRupee, Briefcase, CheckCircle2, Clock, ArrowUpRight, Menu, X, Upload, Download,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Seo from "@/components/Seo";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STEPS = ["Applied", "Under Review", "Shortlisted", "Interview Scheduled", "Selected", "Joined"];
const STATUS_COLORS = {
  "Applied": "bg-slate-100 text-slate-700 border-slate-200",
  "Under Review": "bg-amber-50 text-amber-700 border-amber-200",
  "Shortlisted": "bg-blue-50 text-blue-700 border-blue-200",
  "Interview Scheduled": "bg-violet-50 text-violet-700 border-violet-200",
  "Selected": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Joined": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Rejected": "bg-rose-50 text-rose-700 border-rose-200",
};

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "applications", label: "Applications", icon: FileText },
  { key: "saved", label: "Saved Jobs", icon: Bookmark },
  { key: "recommended", label: "Recommended", icon: Sparkles },
  { key: "interviews", label: "Interviews", icon: CalendarClock },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "profile", label: "Profile", icon: User },
];

function StatusBadge({ status }) {
  return <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${STATUS_COLORS[status] || STATUS_COLORS.Applied}`}>{status || "Applied"}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function JobRow({ job, right }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-shadow">
      <div className="min-w-0">
        <Link to={`/job/${job.slug}`} className="font-semibold text-slate-800 hover:text-brand-emerald truncate block">{job.title}</Link>
        <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><MapPin size={13} />{job.location}</span>
          <span className="flex items-center gap-1"><Briefcase size={13} />{job.experience || "—"}</span>
          <span className="flex items-center gap-1"><IndianRupee size={13} />{job.salary || "Negotiable"}</span>
        </div>
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}

export default function CandidateDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [apps, setApps] = useState([]);
  const [saved, setSaved] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [form, setForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);

  const onResume = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingResume(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.put("/candidate/me", { resume_file_id: up.data.file_id });
      const p = await api.get("/candidate/me");
      setProfile(p.data);
      toast.success("Resume updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingResume(false); }
  };

  useEffect(() => { document.documentElement.classList.add("theme-light"); return () => document.documentElement.classList.remove("theme-light"); }, []);

  const load = useCallback(async () => {
    try {
      const [p, a, s, r, i, n] = await Promise.all([
        api.get("/candidate/me"), api.get("/candidate/applications"), api.get("/candidate/saved-jobs"),
        api.get("/candidate/recommended"), api.get("/candidate/interviews"), api.get("/candidate/notifications"),
      ]);
      setProfile(p.data);
      setForm({
        name: p.data.name || "", phone: p.data.phone || "", current_location: p.data.current_location || "",
        qualification: p.data.qualification || "", experience: p.data.experience || "", current_job: p.data.current_job || "",
        preferred_job: p.data.preferred_job || "", skills: (p.data.skills || []).join(", "),
        preferred_location: p.data.preferred_location || "", expected_salary: p.data.expected_salary || "",
        employment_type: p.data.employment_type || "", languages: p.data.languages || "", industry: p.data.industry || "",
      });
      setApps(a.data); setSaved(s.data); setRecommended(r.data); setInterviews(i.data); setNotifs(n.data);
    } catch (err) { /* handled by interceptor */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unsave = async (slug) => {
    await api.delete(`/candidate/saved-jobs/${slug}`);
    setSaved((prev) => prev.filter((j) => j.slug !== slug));
    toast.success("Removed from saved");
  };
  const saveRec = async (slug) => {
    await api.post(`/candidate/saved-jobs/${slug}`);
    toast.success("Job saved");
    const s = await api.get("/candidate/saved-jobs"); setSaved(s.data);
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.put("/candidate/me", form);
      setProfile(data);
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
    finally { setSavingProfile(false); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const profileFields = ["name", "phone", "current_location", "qualification", "experience", "current_job", "preferred_job", "preferred_location", "expected_salary", "employment_type", "industry", "languages"];
  const filled = profileFields.filter((k) => (form[k] || "").toString().trim()).length + ((form.skills || "").trim() ? 1 : 0);
  const completeness = Math.round((filled / (profileFields.length + 1)) * 100);

  const stats = [
    { label: "Applications", value: apps.length, icon: FileText, tab: "applications" },
    { label: "Saved Jobs", value: saved.length, icon: Bookmark, tab: "saved" },
    { label: "Interviews", value: interviews.length, icon: CalendarClock, tab: "interviews" },
    { label: "Recommended", value: recommended.length, icon: Sparkles, tab: "recommended" },
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-slate-800 flex">
      <Seo title="Candidate Dashboard" noindex />
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <Link to="/" className="flex items-center h-16 px-5 border-b border-slate-200">
          <img src="/logo.png" alt="Dreamzz HR" className="h-9 w-auto" />
        </Link>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => {
            const I = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`cand-tab-${t.key}`}
                onClick={() => { setTab(t.key); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-brand-emerald text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <I size={18} />{t.label}
                {t.key === "notifications" && notifs.length > 0 && (
                  <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-brand-emerald text-white"}`}>{notifs.length}</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <button data-testid="cand-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50">
            <LogOut size={18} />Logout
          </button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)} aria-label="Open menu"><Menu size={22} /></button>
          <div>
            <h1 className="font-display font-bold text-lg leading-none capitalize">{TABS.find((t) => t.key === tab)?.label}</h1>
            <p className="text-xs text-slate-500 mt-0.5">Welcome back, {user?.name?.split(" ")[0] || "Candidate"}</p>
          </div>
          <Link to="/jobs" className="ml-auto"><Button className="bg-brand-emerald hover:bg-[#C62828] text-white h-9">Browse Jobs</Button></Link>
        </header>

        <main className="flex-1 p-4 sm:p-6 max-w-5xl w-full">
          {tab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((s) => {
                  const I = s.icon;
                  return (
                    <button key={s.label} onClick={() => setTab(s.tab)} className="text-left p-4 rounded-2xl bg-white border border-slate-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-emerald/10 text-brand-emerald"><I size={20} /></span>
                        <span className="font-display font-extrabold text-2xl">{s.value}</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-500">{s.label}</p>
                    </button>
                  );
                })}
              </div>

              <div className="p-5 rounded-2xl bg-white border border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold">Profile Completeness</h2>
                  <span className="font-display font-bold text-brand-emerald">{completeness}%</span>
                </div>
                <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-brand-emerald transition-all" style={{ width: `${completeness}%` }} />
                </div>
                {completeness < 100 && (
                  <button onClick={() => setTab("profile")} className="mt-3 text-sm text-brand-emerald font-medium inline-flex items-center gap-1">Complete your profile <ArrowUpRight size={14} /></button>
                )}
              </div>

              <div className="p-5 rounded-2xl bg-white border border-slate-200">
                <h2 className="font-semibold mb-4">Recent Applications</h2>
                {apps.length === 0 ? (
                  <p className="text-sm text-slate-500">You haven't applied to any jobs yet. <Link to="/jobs" className="text-brand-emerald">Browse jobs →</Link></p>
                ) : (
                  <div className="space-y-3">
                    {apps.slice(0, 4).map((a) => (
                      <div key={a.id} className="flex items-center justify-between gap-4 p-3 rounded-xl bg-slate-50">
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{a.job_title || "Job"}</p>
                          <p className="text-xs text-slate-500">Applied {fmtDate(a.created_at)}</p>
                        </div>
                        <StatusBadge status={a.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "applications" && (
            <div className="space-y-3">
              {apps.length === 0 ? (
                <EmptyState icon={FileText} title="No applications yet" body="Apply to jobs and track their status here." cta />
              ) : apps.map((a) => (
                <div key={a.id} className="p-4 rounded-xl bg-white border border-slate-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      {a.job_slug ? <Link to={`/job/${a.job_slug}`} className="font-semibold hover:text-brand-emerald">{a.job_title || "Job"}</Link> : <span className="font-semibold">{a.job_title || "Job"}</span>}
                      <p className="text-xs text-slate-500 mt-1">Applied {fmtDate(a.created_at)}</p>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                  {a.status !== "Rejected" && (
                    <div className="mt-4 flex items-center gap-1 overflow-x-auto">
                      {STATUS_STEPS.map((st, idx) => {
                        const curIdx = STATUS_STEPS.indexOf(a.status || "Applied");
                        const done = idx <= curIdx;
                        return (
                          <React.Fragment key={st}>
                            <div className="flex flex-col items-center min-w-[64px]">
                              <span className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold ${done ? "bg-brand-emerald text-white" : "bg-slate-200 text-slate-400"}`}>
                                {done ? <CheckCircle2 size={14} /> : idx + 1}
                              </span>
                              <span className={`mt-1 text-[9px] text-center ${done ? "text-slate-700" : "text-slate-400"}`}>{st}</span>
                            </div>
                            {idx < STATUS_STEPS.length - 1 && <div className={`h-0.5 flex-1 min-w-[8px] ${idx < curIdx ? "bg-brand-emerald" : "bg-slate-200"}`} />}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "saved" && (
            <div className="space-y-3">
              {saved.length === 0 ? (
                <EmptyState icon={Bookmark} title="No saved jobs" body="Tap the bookmark on any job to save it for later." cta />
              ) : saved.map((j) => (
                <JobRow key={j.slug} job={j} right={<Button variant="outline" className="h-9 border-slate-200 text-rose-600" onClick={() => unsave(j.slug)}>Remove</Button>} />
              ))}
            </div>
          )}

          {tab === "recommended" && (
            <div className="space-y-3">
              {recommended.length === 0 ? (
                <EmptyState icon={Sparkles} title="No recommendations yet" body="Complete your profile with skills and preferred role to get matched." />
              ) : recommended.map((j) => (
                <JobRow key={j.slug} job={j} right={
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="h-9 border-slate-200" onClick={() => saveRec(j.slug)}>Save</Button>
                    <Link to={`/job/${j.slug}`}><Button className="h-9 bg-brand-emerald hover:bg-[#C62828] text-white">View</Button></Link>
                  </div>
                } />
              ))}
            </div>
          )}

          {tab === "interviews" && (
            <div className="space-y-3">
              {interviews.length === 0 ? (
                <EmptyState icon={CalendarClock} title="No interviews scheduled" body="When a recruiter schedules an interview, it will appear here." />
              ) : interviews.map((iv) => (
                <div key={iv.id} className="p-4 rounded-xl bg-white border border-slate-200 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold">{iv.job || iv.job_title || "Interview"}</p>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-600">
                      <span className="flex items-center gap-1"><CalendarClock size={14} className="text-brand-emerald" />{fmtDate(iv.date)} {iv.time || ""}</span>
                      <span className="flex items-center gap-1"><MapPin size={14} className="text-brand-emerald" />{iv.mode || "—"} {iv.location || ""}</span>
                    </div>
                    {iv.meeting_link && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm text-brand-emerald">Join meeting →</a>}
                    {iv.notes && <p className="mt-2 text-sm text-slate-500">{iv.notes}</p>}
                  </div>
                  <StatusBadge status={iv.status || "Scheduled"} />
                </div>
              ))}
            </div>
          )}

          {tab === "notifications" && (
            <div className="space-y-3">
              {notifs.length === 0 ? (
                <EmptyState icon={Bell} title="No notifications" body="Updates about your applications and interviews will show here." />
              ) : notifs.map((n, i) => (
                <div key={i} className="p-4 rounded-xl bg-white border border-slate-200 flex items-start gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand-emerald/10 text-brand-emerald shrink-0">
                    {n.type === "interview" ? <CalendarClock size={18} /> : <FileText size={18} />}
                  </span>
                  <div>
                    <p className="font-medium text-slate-800">{n.title}</p>
                    <p className="text-sm text-slate-500">{n.body}</p>
                    <p className="text-xs text-slate-400 mt-1">{fmtDate(n.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "profile" && (
            <form onSubmit={saveProfile} className="p-5 sm:p-6 rounded-2xl bg-white border border-slate-200 grid sm:grid-cols-2 gap-4">
              <Fld label="Full Name" k="name" form={form} upd={upd} />
              <Fld label="Mobile" k="phone" form={form} upd={upd} />
              <Fld label="Current Location" k="current_location" form={form} upd={upd} />
              <Fld label="Highest Qualification" k="qualification" form={form} upd={upd} />
              <Fld label="Total Experience" k="experience" form={form} upd={upd} placeholder="e.g. 3 years" />
              <Fld label="Current Job Title" k="current_job" form={form} upd={upd} />
              <Fld label="Preferred Job Role" k="preferred_job" form={form} upd={upd} />
              <Fld label="Preferred Location" k="preferred_location" form={form} upd={upd} />
              <Fld label="Expected Salary" k="expected_salary" form={form} upd={upd} placeholder="e.g. ₹25,000 / month" />
              <Fld label="Employment Type" k="employment_type" form={form} upd={upd} placeholder="Full-time / Contract" />
              <Fld label="Preferred Industry" k="industry" form={form} upd={upd} />
              <Fld label="Languages Known" k="languages" form={form} upd={upd} placeholder="Kannada, English, Hindi" />
              <div className="sm:col-span-2">
                <Label>Skills <span className="text-slate-400 font-normal">(comma separated)</span></Label>
                <Textarea data-testid="cand-skills" value={form.skills} onChange={upd("skills")} className="mt-1 border-slate-200" rows={2} placeholder="Front Office, MS Office, Communication" />
              </div>
              <div className="sm:col-span-2">
                <Label>Resume</Label>
                <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-3">
                  {profile?.resume_file_id ? (
                    <a data-testid="cand-resume-download" href={`${API}/files/${profile.resume_file_id}?auth=${getToken()}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-brand-emerald">
                      <Download size={16} className="text-brand-emerald" />{profile.resume_filename || "View current resume"}
                    </a>
                  ) : (
                    <span className="text-sm text-slate-400">No resume uploaded yet.</span>
                  )}
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-slate-300 cursor-pointer hover:border-brand-emerald text-sm text-slate-600 w-fit">
                    <Upload size={16} className="text-brand-emerald" />{uploadingResume ? "Uploading..." : profile?.resume_file_id ? "Replace resume" : "Upload resume"}
                    <input data-testid="cand-resume-upload" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onResume} disabled={uploadingResume} />
                  </label>
                </div>
              </div>
              <div className="sm:col-span-2 flex items-center justify-end pt-2">
                <Button data-testid="cand-save-profile" type="submit" disabled={savingProfile} className="bg-brand-emerald hover:bg-[#C62828] text-white h-11 px-6">
                  {savingProfile ? "Saving..." : "Save Profile"}
                </Button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}

function Fld({ label, k, form, upd, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input data-testid={`cand-${k.replace(/_/g, "-")}`} value={form[k] || ""} onChange={upd(k)} placeholder={placeholder} className="mt-1 border-slate-200" />
    </div>
  );
}

function EmptyState({ icon: I, title, body, cta }) {
  return (
    <div className="text-center py-16 rounded-2xl bg-white border border-dashed border-slate-200">
      <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-brand-emerald/10 text-brand-emerald"><I size={26} /></span>
      <h3 className="mt-4 font-display font-bold text-lg">{title}</h3>
      <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">{body}</p>
      {cta && <Link to="/jobs"><Button className="mt-5 bg-brand-emerald hover:bg-[#C62828] text-white">Browse Jobs</Button></Link>}
    </div>
  );
}
